import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, type UIMessage } from "ai";
import { X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Navigate, useLocation, useNavigate, useParams } from "react-router";

import {
  parseBrandAssistantActivityUpdatePart,
  type BrandAssistantCreationPreviewToolName,
  type BrandAssistantActivityUpdateData,
} from "@runcheetah/shared";

import {
  appendAssistantLiveActivityUpdate,
  AssistantChatPanel,
  getLatestAssistantMessageAfterLatestUser,
  getLatestAssistantTipsAfterLatestUser,
  getPersistedAssistantStreamActivityUpdates,
  hasActiveAssistantMessagePart,
  hasActivePersistedAssistantStream,
  shouldRenderAssistantStructuredExtras,
} from "@/components/chat/assistant-chat";
import { FloatingRunsBalance } from "@/components/billing/floating-runs-balance";
import { AssistantLiveActivity } from "@/components/chat/assistant-live-activity";
import { AssistantOnboardingCompletionCards } from "@/components/chat/assistant-onboarding-completion-cards";
import { AssistantBrandedImageGenerationPreviews } from "@/components/chat/assistant-branded-image-generation-previews";
import { AssistantVisualUniverseRegenerationPreviews } from "@/components/chat/assistant-visual-universe-regeneration-previews";
import {
  AssistantSuggestionPills,
  QUICK_PROMPTS,
  QuickPromptButton,
} from "@/components/chat/assistant-quick-prompts";
import { AssistantBrandProfileEditPreviews } from "@/components/chat/assistant-brand-profile-edit-previews";
import { AssistantAudienceProfileCreationPreviews } from "@/components/chat/assistant-audience-profile-creation-previews";
import { AssistantChatMediaGalleryPanel } from "@/components/chat/assistant-chat-media-gallery-panel";
import { AssistantGeneratedAssetImageFineTunePreviews } from "@/components/chat/assistant-generated-asset-image-fine-tune-previews";
import { AssistantGeneratedAssetCopyEditPreviews } from "@/components/chat/assistant-generated-asset-copy-edit-previews";
import { AssistantCampaignGenerationPreviews } from "@/components/chat/assistant-campaign-generation-previews";
import { AssistantStandaloneAssetBatchPreviews } from "@/components/chat/assistant-standalone-asset-batch-previews";
import { AssistantSocialPostPreviews } from "@/components/chat/assistant-social-post-previews";
import { SocialPostPreview } from "@/components/campaign/social-post-preview";
import { CenteredLoader } from "@/components/common";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { useAssetCreationGate } from "@/hooks/use-asset-creation-gate";
import { useMarkBrandAssistantChatSeen } from "@/hooks/use-mark-brand-assistant-chat-seen";
import { buildBrandPath, getUnavailableBrandPath, useRouteBrand } from "@/hooks/use-route-brand";
import { ASSISTANT_ROUTE_CATALOG } from "@/lib/app-route-manifest";
import {
  ASSISTANT_CAMPAIGN_GENERATION_STATE_CHANGE_EVENT,
  getAssistantCampaignGenerationPreviewReferences,
  getStartedAssistantCampaignGenerations,
  isAssistantCampaignGenerationProposalStale,
} from "@/lib/assistant-campaign-generation-previews";
import {
  getAssistantStandaloneAssetBatchPreviewReferences,
  isAssistantStandaloneAssetBatchProposalStale,
} from "@/lib/assistant-standalone-asset-batch-previews";
import {
  getAssistantBrandedImageGenerationPreviewReferences,
  isAssistantBrandedImageGenerationProposalStale,
} from "@/lib/assistant-branded-image-generation-previews";
import {
  getAssistantVisualUniverseRegenerationPreviewReferences,
  isAssistantVisualUniverseRegenerationProposalStale,
} from "@/lib/assistant-visual-universe-regeneration-previews";
import {
  getAssistantGeneratedAssetImageFineTunePreviewReferences,
  isAssistantGeneratedAssetImageFineTuneProposalStale,
} from "@/lib/assistant-generated-asset-image-fine-tune-previews";
import {
  getAssistantGeneratedAssetCopyEditPreviewReferences,
  isAssistantGeneratedAssetCopyEditProposalStale,
  type AssistantGeneratedAssetCopyEditPreviewReference,
} from "@/lib/assistant-generated-asset-copy-edit-previews";
import {
  buildBrandAssistantSendRequestOptionsBody,
  buildBrandAssistantSendMessagesRequestBody,
  buildBrandAssistantUserPromptParts,
  getBrandAssistantSendRequestContext,
  type BrandAssistantSelectedAssetReference,
  type BrandAssistantSelectedImageReference,
} from "@/lib/brand-assistant-chat-request";
import {
  buildSelectedAssetReference,
  buildSelectedImageReference,
} from "@/lib/brand-assistant-selected-references";
import {
  getAssistantBrandProfileEditPreviewReferences,
  getAssistantBrandProfileEditPreviewResolutionStatuses,
  getLatestOnboardingBrandProfileEditPreviewId,
} from "@/lib/assistant-brand-profile-edit-previews";
import {
  getAssistantAudienceProfileCreationPreviewReferences,
  isAssistantAudienceProfileCreationProposalStale,
} from "@/lib/assistant-audience-profile-creation-previews";
import { getAssistantOnboardingCompletionReferences } from "@/lib/assistant-onboarding-completion";
import { getAssistantSocialPostPreviewReferences } from "@/lib/assistant-social-post-previews";
import {
  buildBrandAssistantChatPath,
  type BrandAssistantCreatedChatTransition,
  type BrandAssistantChatSummary,
  getBrandAssistantChatRuntimeKey,
  getLatestBrandAssistantUserMessage,
  getRetryableBrandAssistantUserMessage,
  isBrandAssistantResponsePersisted,
  isBrandAssistantPendingChatRoute,
  PENDING_BRAND_ASSISTANT_CHAT_ID,
  resolveBrandAssistantChatSnapshotMessages,
  shouldRedirectUnavailableBrandAssistantChat,
  shouldResetBrandAssistantChatState,
} from "@/lib/brand-assistant-chat-history";
import { CHEETAH_SQUARE_LOGO_SRC } from "@/lib/brand-assets";
import { getPulseGreetingPeriod } from "@/lib/pulse-display";
import { resolveAssistantSelectedAssetPreview } from "@/lib/assistant-selected-asset-preview";
import { buildFocusedPostEditorPath } from "@/lib/focused-post-editor";
import { formatInstagramProfileLabel } from "@/lib/social-post-chrome";
import { getBrandSwitcherTitle } from "@/lib/brand-switcher";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import type {
  ChatGeneratedAsset,
  ChatGeneratedAssetLogoAsset,
  ChatGeneratedImage,
} from "@/components/chat/assistant-chat-media-gallery-types";

const EMPTY_MESSAGES: UIMessage[] = [];
const EMPTY_GENERATED_ASSETS: ChatGeneratedAsset[] = [];
const EMPTY_LOGO_ASSETS: ChatGeneratedAssetLogoAsset[] = [];
const PENDING_BRAND_ID = "00000000-0000-4000-8000-000000000000";
const SIDEBAR_CHAT_PAGE_SIZE = 3;
const CHAT_RESPONSE_PERSISTENCE_ATTEMPTS = 12;
const CHAT_RESPONSE_PERSISTENCE_INTERVAL_MS = 250;

export default function PulsePage() {
  const intl = useIntl();
  const { user } = useAuthenticatedUser();
  const { chatId } = useParams<{ chatId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const { brand, brandQuery } = useRouteBrand();
  const assetCreationGate = useAssetCreationGate(brand?.id);
  const brandId = brand?.id ?? PENDING_BRAND_ID;
  const brandTitle = brand
    ? getBrandSwitcherTitle(brand)
    : intl.formatMessage({
        id: "pulse.brandFallback",
        defaultMessage: "this brand",
      });
  const instagramProfileLabel = formatInstagramProfileLabel(brand?.sourceDomain);
  const [draft, setDraft] = useState("");
  const [selectedAssetReferences, setSelectedAssetReferences] = useState<
    BrandAssistantSelectedAssetReference[]
  >([]);
  const [selectedImageReferences, setSelectedImageReferences] = useState<
    BrandAssistantSelectedImageReference[]
  >([]);
  const [campaignGenerationStateVersion, setCampaignGenerationStateVersion] = useState(0);
  const [liveActivityUpdates, setLiveActivityUpdates] = useState<
    BrandAssistantActivityUpdateData[]
  >([]);
  const [submissionError, setSubmissionError] = useState<Error | null>(null);
  const [isFinalizingResponse, setIsFinalizingResponse] = useState(false);
  const [createdChatTransition, setCreatedChatTransition] =
    useState<BrandAssistantCreatedChatTransition | null>(null);
  const [selectedAssetPreview, setSelectedAssetPreview] = useState<ChatGeneratedAsset | null>(null);
  const lastRequestErrorRef = useRef<Error | null>(null);
  const lastResetChatRef = useRef<string | null>(null);
  const appReady = brand?.status === "scanned";
  const routeChatIsPending = isBrandAssistantPendingChatRoute(chatId);

  const chatListQuery = trpc.brandAssistantChat.list.useQuery(
    { brandId },
    {
      enabled: appReady,
      staleTime: 15_000,
      refetchOnWindowFocus: true,
    },
  );
  const createChatMutation = trpc.brandAssistantChat.create.useMutation();
  const chatQuery = trpc.brandAssistantChat.get.useQuery(
    { brandId, chatId: chatId ?? PENDING_BRAND_ASSISTANT_CHAT_ID },
    {
      enabled: appReady && Boolean(chatId) && !routeChatIsPending,
      staleTime: 0,
      refetchOnWindowFocus: true,
    },
  );
  const generatedAssetsQuery = trpc.brandAssistantChat.generatedAssets.useQuery(
    { brandId, chatId: chatId ?? PENDING_BRAND_ASSISTANT_CHAT_ID },
    {
      enabled: appReady && Boolean(chatId) && !routeChatIsPending,
      refetchOnWindowFocus: true,
    },
  );
  const logosQuery = trpc.brand.listLogos.useQuery(
    { brandId },
    { enabled: appReady && Boolean(chatId) && !routeChatIsPending, staleTime: 0 },
  );
  const generatedAssetsForPreview = generatedAssetsQuery.data?.assets ?? EMPTY_GENERATED_ASSETS;
  const selectedAssetById = useMemo(
    () =>
      new Map(generatedAssetsForPreview.map((asset) => [asset.generatedAssetId, asset] as const)),
    [generatedAssetsForPreview],
  );
  const logoAssets = useMemo(
    () =>
      (logosQuery.data?.logos ?? EMPTY_LOGO_ASSETS).map((logo) => ({
        objectKey: logo.objectKey,
        signedUrl: logo.signedUrl,
        isPrimary: logo.isPrimary,
      })),
    [logosQuery.data?.logos],
  );

  useEffect(() => {
    if (
      shouldRedirectUnavailableBrandAssistantChat({
        chatId: routeChatIsPending ? undefined : chatId,
        chat: chatQuery.data?.chat,
        isError: chatQuery.isError,
      })
    ) {
      navigate(buildBrandPath(brandId, "/pulse"), { replace: true });
    }
  }, [brandId, chatId, chatQuery.data?.chat, chatQuery.isError, navigate, routeChatIsPending]);

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: `${import.meta.env.VITE_API_URL ?? ""}/api/chat/brand-assistant`,
        credentials: "include",
        prepareSendMessagesRequest({ messages, trigger, body }) {
          const requestContext = getBrandAssistantSendRequestContext(body);

          return {
            body: buildBrandAssistantSendMessagesRequestBody({
              brandId,
              chatId:
                requestContext?.chatId ??
                (routeChatIsPending ? PENDING_BRAND_ASSISTANT_CHAT_ID : chatId) ??
                PENDING_BRAND_ASSISTANT_CHAT_ID,
              routeCatalog: ASSISTANT_ROUTE_CATALOG,
              trigger,
              messages,
              selectedAssetReferences: requestContext?.selectedAssetReferences,
              selectedImageReferences: requestContext?.selectedImageReferences,
            }),
          };
        },
      }),
    [brandId, chatId, routeChatIsPending],
  );

  const chatData = chatQuery.data as
    | {
        chat: {
          id: string;
          messages: UIMessage[];
          assistantContentVersion: number;
          seenAssistantContentVersion: number;
        } | null;
      }
    | undefined;
  const activeChat = chatData?.chat ?? null;
  useMarkBrandAssistantChatSeen({ brandId, chat: activeChat });
  const initialMessages = activeChat?.messages ?? EMPTY_MESSAGES;
  const chatRuntimeKey = getBrandAssistantChatRuntimeKey({
    chatId,
    routeChatIsPending,
    pendingLocationKey: location.key,
    createdChatTransition,
  });
  const { messages, sendMessage, regenerate, status, stop, error, setMessages, clearError } =
    useChat({
      id: `brand-assistant-${brandId}-${chatRuntimeKey}`,
      messages: initialMessages,
      transport,
      experimental_throttle: 250,
      onError(requestError) {
        lastRequestErrorRef.current = requestError;
      },
      onData(dataPart) {
        const update = parseBrandAssistantActivityUpdatePart(dataPart)?.data;
        if (update) {
          setLiveActivityUpdates((currentUpdates) =>
            appendAssistantLiveActivityUpdate(currentUpdates, update),
          );
        }
      },
    });
  const messagesRef = useRef(messages);
  const isBusy = status === "submitted" || status === "streaming";
  const hasPersistedStream = hasActivePersistedAssistantStream(messages);
  const activeChatHasPersistedStream = hasActivePersistedAssistantStream(
    activeChat?.messages ?? EMPTY_MESSAGES,
  );
  const effectiveStatus =
    isBusy || hasPersistedStream || isFinalizingResponse ? "streaming" : status;
  const isChatBusy = isBusy || hasPersistedStream || isFinalizingResponse;
  const latestAssistantMessage = useMemo(
    () => getLatestAssistantMessageAfterLatestUser(messages),
    [messages],
  );
  const latestAssistantTips = useMemo(
    () => getLatestAssistantTipsAfterLatestUser(messages),
    [messages],
  );
  const persistedStreamActivityUpdates = useMemo(
    () => getPersistedAssistantStreamActivityUpdates(messages),
    [messages],
  );
  const startedCampaignGenerations = useMemo(() => {
    // Recompute when cross-component campaign-generation state changes.
    void campaignGenerationStateVersion;
    return getStartedAssistantCampaignGenerations({
      brandId,
      messages,
    });
  }, [brandId, campaignGenerationStateVersion, messages]);

  useEffect(() => {
    const refreshStartedCampaignGenerations = () =>
      setCampaignGenerationStateVersion((version) => version + 1);

    window.addEventListener(
      ASSISTANT_CAMPAIGN_GENERATION_STATE_CHANGE_EVENT,
      refreshStartedCampaignGenerations,
    );
    return () =>
      window.removeEventListener(
        ASSISTANT_CAMPAIGN_GENERATION_STATE_CHANGE_EVENT,
        refreshStartedCampaignGenerations,
      );
  }, []);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    if (!isBusy) {
      setLiveActivityUpdates([]);
    }
  }, [isBusy]);

  useEffect(() => {
    if (!chatId || routeChatIsPending || !hasPersistedStream) {
      return;
    }

    const interval = window.setInterval(() => {
      void utils.brandAssistantChat.get.invalidate({ brandId, chatId });
      void utils.brandAssistantChat.list.invalidate({ brandId });
    }, 1_000);

    return () => window.clearInterval(interval);
  }, [
    brandId,
    chatId,
    hasPersistedStream,
    routeChatIsPending,
    utils.brandAssistantChat.get,
    utils.brandAssistantChat.list,
  ]);

  useEffect(() => {
    if (isBusy || !activeChat || (!hasPersistedStream && !activeChatHasPersistedStream)) {
      return;
    }

    setMessages(
      resolveBrandAssistantChatSnapshotMessages({
        currentMessages: messagesRef.current,
        nextMessages: activeChat.messages as unknown as UIMessage[],
      }),
    );
  }, [activeChat, activeChatHasPersistedStream, hasPersistedStream, isBusy, setMessages]);
  const profilePreviewResolutionStatuses = useMemo(
    () => getAssistantBrandProfileEditPreviewResolutionStatuses(messages),
    [messages],
  );
  const activeOnboardingProfilePreviewId = useMemo(
    () => getLatestOnboardingBrandProfileEditPreviewId(messages),
    [messages],
  );

  useEffect(() => {
    if (!chatId) {
      lastResetChatRef.current = null;
      return;
    }

    if (createdChatTransition?.chatId === chatId) {
      return;
    }

    if (
      !shouldResetBrandAssistantChatState({
        chatId,
        loadedChatId: activeChat?.id,
        lastResetChatId: lastResetChatRef.current,
        isBusy,
      }) ||
      !activeChat
    ) {
      return;
    }

    lastResetChatRef.current = activeChat.id;
    setMessages(activeChat.messages as unknown as UIMessage[]);
    setDraft("");
    setSelectedAssetReferences([]);
    setSelectedImageReferences([]);
    setSelectedAssetPreview(null);
    setLiveActivityUpdates([]);
    setSubmissionError(null);
    clearError();
  }, [activeChat, chatId, clearError, createdChatTransition?.chatId, isBusy, setMessages]);

  const liveActivity = useMemo(() => {
    const updates =
      liveActivityUpdates.length > 0 ? liveActivityUpdates : persistedStreamActivityUpdates;
    return isChatBusy && updates.length > 0 ? <AssistantLiveActivity updates={updates} /> : null;
  }, [isChatBusy, liveActivityUpdates, persistedStreamActivityUpdates]);

  const renderSelectedAssetReference = useCallback(
    (reference: BrandAssistantSelectedAssetReference) => {
      const asset = selectedAssetById.get(reference.generatedAssetId);
      return asset ? (
        <SelectedAssetVisual asset={asset} logoAssets={logoAssets} variant="message" />
      ) : null;
    },
    [logoAssets, selectedAssetById],
  );
  if (brandQuery.isPending) {
    return <CenteredLoader />;
  }

  if (!brand || !appReady) {
    return <Navigate to={getUnavailableBrandPath(brand?.id)} replace />;
  }

  const firstName =
    user?.name?.split(/\s+/).filter(Boolean)[0] ??
    intl.formatMessage({
      id: "pulse.greetingNameFallback",
      defaultMessage: "there",
    });
  const greetingPeriod = getPulseGreetingPeriod();
  const hasMessages = messages.length > 0;
  const pendingChatReady = routeChatIsPending || (!chatId && Boolean(chatListQuery.data));
  const activeChatReady = pendingChatReady || Boolean(chatId && chatQuery.data?.chat);
  const isPreparingChat =
    (!chatId && chatListQuery.isPending) ||
    createChatMutation.isPending ||
    (Boolean(chatId) && !routeChatIsPending && chatQuery.isPending);
  const greetingTitle =
    greetingPeriod === "morning"
      ? intl.formatMessage(
          {
            id: "pulse.emptyState.title.morning",
            defaultMessage: "Good morning, {firstName}",
          },
          { firstName },
        )
      : greetingPeriod === "afternoon"
        ? intl.formatMessage(
            {
              id: "pulse.emptyState.title.afternoon",
              defaultMessage: "Good afternoon, {firstName}",
            },
            { firstName },
          )
        : intl.formatMessage(
            {
              id: "pulse.emptyState.title.evening",
              defaultMessage: "Good evening, {firstName}",
            },
            { firstName },
          );

  const refreshActiveChat = async (activeChatId: string) => {
    await utils.brandAssistantChat.get.invalidate({
      brandId: brand.id,
      chatId: activeChatId,
    });
    const refreshedChat = await utils.brandAssistantChat.get.fetch({
      brandId: brand.id,
      chatId: activeChatId,
    });
    if (refreshedChat.chat) {
      updateCachedChatListItem(refreshedChat.chat);
      await Promise.all([
        utils.brandAssistantChat.list.invalidate({ brandId: brand.id }),
        utils.brandAssistantChat.list.invalidate({
          brandId: brand.id,
          limit: SIDEBAR_CHAT_PAGE_SIZE,
        }),
      ]);
    }
    return refreshedChat.chat;
  };

  const waitForPersistedResponse = async (activeChatId: string, userMessageId: string) => {
    let latestChat: Awaited<ReturnType<typeof refreshActiveChat>> = null;
    for (let attempt = 0; attempt < CHAT_RESPONSE_PERSISTENCE_ATTEMPTS; attempt += 1) {
      latestChat = await refreshActiveChat(activeChatId);
      if (
        latestChat &&
        isBrandAssistantResponsePersisted({
          messages: latestChat.messages as unknown as UIMessage[],
          userMessageId,
        })
      ) {
        return latestChat;
      }

      if (attempt < CHAT_RESPONSE_PERSISTENCE_ATTEMPTS - 1) {
        await new Promise((resolve) =>
          window.setTimeout(resolve, CHAT_RESPONSE_PERSISTENCE_INTERVAL_MS),
        );
      }
    }
    return latestChat;
  };

  const updateCachedChatListItem = (chat: BrandAssistantChatSummary) => {
    const updateList = (input: { brandId: string; limit?: number }) => {
      utils.brandAssistantChat.list.setData(input, (current) =>
        current
          ? {
              ...current,
              chats: current.chats.map((item) =>
                item.id === chat.id ? { ...item, ...chat } : item,
              ),
            }
          : current,
      );
    };

    updateList({ brandId: brand.id });
    updateList({ brandId: brand.id, limit: SIDEBAR_CHAT_PAGE_SIZE });
  };

  const insertOptimisticChatListItem = (
    chat: NonNullable<Awaited<ReturnType<typeof createChatMutation.mutateAsync>>["chat"]>,
  ) => {
    const updateList = (input: { brandId: string; limit?: number }) => {
      utils.brandAssistantChat.list.setData(input, (current) => {
        if (!current) {
          return { chats: [chat], hasUnseenAssistantContent: false, nextCursor: null };
        }

        return {
          ...current,
          chats: [chat, ...current.chats.filter((item) => item.id !== chat.id)],
        };
      });
    };

    updateList({ brandId: brand.id });
    updateList({ brandId: brand.id, limit: SIDEBAR_CHAT_PAGE_SIZE });
  };

  const seedOptimisticActiveChat = (
    chat: NonNullable<Awaited<ReturnType<typeof createChatMutation.mutateAsync>>["chat"]>,
  ) => {
    const setChatData = utils.brandAssistantChat.get.setData as (
      input: { brandId: string; chatId: string },
      data: { chat: unknown },
    ) => void;

    setChatData(
      { brandId: brand.id, chatId: chat.id },
      {
        chat,
      },
    );
  };

  const sendPrompt = async (
    prompt: string,
    overrides?: {
      selectedAssetReferences?: BrandAssistantSelectedAssetReference[];
      selectedImageReferences?: BrandAssistantSelectedImageReference[];
      creationTool?: BrandAssistantCreationPreviewToolName | null;
    },
  ) => {
    if (!activeChatReady || isChatBusy) {
      return;
    }

    const selectedAssetReferencesForPrompt =
      overrides?.selectedAssetReferences ?? selectedAssetReferences;
    const selectedImageReferencesForPrompt =
      overrides?.selectedImageReferences ?? selectedImageReferences;
    const promptParts = buildBrandAssistantUserPromptParts({
      prompt,
      selectedAssetReferences: selectedAssetReferencesForPrompt,
      selectedImageReferences: selectedImageReferencesForPrompt,
      creationTool: overrides?.creationTool,
    });
    const userMessage = {
      id: `msg_${crypto.randomUUID()}`,
      role: "user" as const,
      parts: promptParts,
    };

    let activeChatId = routeChatIsPending ? null : chatId;
    if (!activeChatId) {
      let createdChat: Awaited<ReturnType<typeof createChatMutation.mutateAsync>>;
      try {
        createdChat = await createChatMutation.mutateAsync({
          brandId: brand.id,
          initialMessage: userMessage,
        });
      } catch (createError) {
        setDraft(prompt);
        setSubmissionError(
          createError instanceof Error
            ? createError
            : new Error(
                intl.formatMessage({
                  id: "common.somethingWentWrong",
                  defaultMessage: "Something went wrong.",
                }),
              ),
        );
        return;
      }

      activeChatId = createdChat.chat.id;
      const transition = { chatId: activeChatId, runtimeKey: chatRuntimeKey };
      setCreatedChatTransition(transition);
      insertOptimisticChatListItem(createdChat.chat);
      seedOptimisticActiveChat(createdChat.chat);
      setMessages([userMessage as UIMessage]);
      navigate(buildBrandAssistantChatPath(brand.id, activeChatId), { replace: true });

      setSubmissionError(null);
      clearError();
      lastRequestErrorRef.current = null;
      setSelectedAssetReferences([]);
      setSelectedImageReferences([]);
      setSelectedAssetPreview(null);
      await regenerate({
        messageId: userMessage.id,
        body: buildBrandAssistantSendRequestOptionsBody({
          chatId: activeChatId,
          selectedAssetReferences: selectedAssetReferencesForPrompt,
          selectedImageReferences: selectedImageReferencesForPrompt,
        }),
      });

      const requestFailed = lastRequestErrorRef.current !== null;
      setIsFinalizingResponse(!requestFailed);
      try {
        const refreshedChat = requestFailed
          ? await refreshActiveChat(activeChatId)
          : await waitForPersistedResponse(activeChatId, userMessage.id);
        if (
          !requestFailed &&
          refreshedChat &&
          isBrandAssistantResponsePersisted({
            messages: refreshedChat.messages as unknown as UIMessage[],
            userMessageId: userMessage.id,
          })
        ) {
          lastResetChatRef.current = activeChatId;
          setCreatedChatTransition(null);
        } else if (!requestFailed) {
          setSubmissionError(
            new Error(
              intl.formatMessage({
                id: "assistantChat.responseIncomplete",
                defaultMessage: "Cheetah didn't finish this response.",
              }),
            ),
          );
        }
      } catch {
        // The first user message is already persisted; keep the live state available for retry.
      } finally {
        setIsFinalizingResponse(false);
      }
      return;
    }

    setSubmissionError(null);
    clearError();
    lastRequestErrorRef.current = null;
    const sendMessagePromise = sendMessage(userMessage, {
      body: buildBrandAssistantSendRequestOptionsBody({
        chatId: activeChatId,
        selectedAssetReferences: selectedAssetReferencesForPrompt,
        selectedImageReferences: selectedImageReferencesForPrompt,
      }),
    });
    setSelectedAssetReferences([]);
    setSelectedImageReferences([]);
    setSelectedAssetPreview(null);
    await sendMessagePromise;
    try {
      await refreshActiveChat(activeChatId);
    } catch {
      // The stream error remains visible and the persisted user turn can be retried.
    }
  };

  const useAssetInChat = (asset: ChatGeneratedAsset) => {
    setSelectedImageReferences([]);
    setSelectedAssetPreview(asset);
    setSelectedAssetReferences([buildSelectedAssetReference(asset)]);
  };

  const useAssetCopyInChat = (asset: ChatGeneratedAsset) => {
    useAssetInChat(asset);
    setDraft(
      intl.formatMessage({
        id: "pulse.composer.changeCopyDraft",
        defaultMessage: "Change the description for this asset.",
      }),
    );
  };

  const openCopyEditReferenceInEditor = (
    reference: AssistantGeneratedAssetCopyEditPreviewReference,
  ) => {
    if (!reference.batchSourceType) {
      return;
    }

    navigate(
      buildFocusedPostEditorPath({
        brandId: brand.id,
        owner: { type: reference.batchSourceType, id: reference.batchId },
        postId: reference.scheduledPostId,
      }),
      {
        state: {
          focusedEditorSelection: {
            blockId: reference.blockId,
            generatedAssetId: reference.generatedAssetId,
            slideId: reference.slideId,
            slideIndex: reference.slideIndex,
          },
        },
      },
    );
  };

  const useImageInChat = (image: ChatGeneratedImage) => {
    setSelectedAssetReferences([]);
    setSelectedAssetPreview(null);
    setSelectedImageReferences([buildSelectedImageReference(image)]);
  };

  const useImageAsVisualUniverseSeed = (image: ChatGeneratedImage) => {
    const imageReference = buildSelectedImageReference(image);
    const prompt = intl.formatMessage({
      id: "pulse.mediaGallery.useAsVisualUniverseSeed",
      defaultMessage: "Use as visual universe seed",
    });

    setSelectedAssetReferences([]);
    setSelectedAssetPreview(null);
    setSelectedImageReferences([imageReference]);

    if (!activeChatReady || isChatBusy) {
      setDraft(prompt);
      return;
    }

    void sendPrompt(prompt, {
      selectedAssetReferences: [],
      selectedImageReferences: [imageReference],
    });
  };

  const selectedAssetReference = selectedAssetReferences[0] ?? null;
  const selectedImageReference = selectedImageReferences[0] ?? null;
  const displayedError = submissionError ?? error;
  const retryableUserMessage = !isChatBusy
    ? displayedError
      ? getLatestBrandAssistantUserMessage(messages)
      : getRetryableBrandAssistantUserMessage(messages)
    : null;
  const retryResponse = async () => {
    if (!retryableUserMessage || !chatId || routeChatIsPending || isChatBusy) {
      return;
    }

    setSubmissionError(null);
    clearError();
    lastRequestErrorRef.current = null;
    await regenerate({
      messageId: retryableUserMessage.id,
      body: buildBrandAssistantSendRequestOptionsBody({ chatId }),
    });

    const requestFailed = lastRequestErrorRef.current !== null;
    setIsFinalizingResponse(!requestFailed);
    try {
      const refreshedChat = requestFailed
        ? await refreshActiveChat(chatId)
        : await waitForPersistedResponse(chatId, retryableUserMessage.id);
      if (
        !requestFailed &&
        refreshedChat &&
        isBrandAssistantResponsePersisted({
          messages: refreshedChat.messages as unknown as UIMessage[],
          userMessageId: retryableUserMessage.id,
        })
      ) {
        lastResetChatRef.current = chatId;
        setCreatedChatTransition(null);
      } else if (!requestFailed) {
        setSubmissionError(
          new Error(
            intl.formatMessage({
              id: "assistantChat.responseIncomplete",
              defaultMessage: "Cheetah didn't finish this response.",
            }),
          ),
        );
      }
    } catch {
      // Keep the persisted user turn and retry action available.
    } finally {
      setIsFinalizingResponse(false);
    }
  };
  const selectedReferenceImageSrc =
    selectedAssetReference?.signedUrl ?? selectedImageReference?.signedUrl;
  const composerPlaceholder =
    selectedAssetReference || selectedImageReference
      ? intl.formatMessage({
          id: "pulse.composer.referencedImagePlaceholder",
          defaultMessage: "Ask for a copy or image edit for the selected asset...",
        })
      : intl.formatMessage({
          id: "pulse.composer.placeholder",
          defaultMessage: "Ask Cheetah anything, or describe what you want to make...",
        });
  return (
    <div className="flex h-full min-h-0 flex-col bg-[radial-gradient(circle_at_50%_34%,rgba(190,255,0,0.10),transparent_28%),linear-gradient(180deg,var(--background),var(--background))] lg:flex-row">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {isPreparingChat ? (
          <CenteredLoader />
        ) : (
          <AssistantChatPanel
            messages={messages}
            status={effectiveStatus}
            draft={draft}
            error={displayedError}
            showThreadToolbar={hasMessages}
            threadToolbarActions={<FloatingRunsBalance brandId={brand.id} variant="toolbar" />}
            className="min-h-0 flex-1 bg-transparent shadow-none backdrop-blur-0 xl:shadow-none"
            contentClassName={cn(
              hasMessages
                ? "mx-auto w-full max-w-4xl px-6 py-8"
                : "flex items-center justify-center px-6 pb-20 pt-10",
            )}
            composerContainerClassName="mx-auto w-full max-w-4xl border-t-0 bg-transparent px-4 pb-2 sm:px-6"
            renderAssistantMessageExtra={(message) => {
              const shouldRenderStructuredExtras = shouldRenderAssistantStructuredExtras({
                isBusy: isChatBusy,
                messageId: message.id,
                latestAssistantMessageId: latestAssistantMessage?.id ?? null,
                hasActiveMessagePart: hasActiveAssistantMessagePart(message),
              });
              const profileEditPreviews = shouldRenderStructuredExtras
                ? getAssistantBrandProfileEditPreviewReferences(message).filter(
                    (reference) =>
                      reference.confirmationSource !== "chatbot_onboarding" ||
                      reference.id === activeOnboardingProfilePreviewId,
                  )
                : [];
              const audienceProfileCreationPreviews = shouldRenderStructuredExtras
                ? getAssistantAudienceProfileCreationPreviewReferences(message)
                : [];
              const onboardingCompletions = shouldRenderStructuredExtras
                ? getAssistantOnboardingCompletionReferences(message, {
                    messages,
                    showProgress: false,
                  })
                : [];
              const assetBatchPreviews = shouldRenderStructuredExtras
                ? getAssistantStandaloneAssetBatchPreviewReferences(message)
                : [];
              const campaignGenerationPreviews = shouldRenderStructuredExtras
                ? getAssistantCampaignGenerationPreviewReferences(message)
                : [];
              const imageGenerationPreviews = shouldRenderStructuredExtras
                ? getAssistantBrandedImageGenerationPreviewReferences(message)
                : [];
              const visualUniverseRegenerationPreviews = shouldRenderStructuredExtras
                ? getAssistantVisualUniverseRegenerationPreviewReferences(message)
                : [];
              const assetImageFineTunePreviews = shouldRenderStructuredExtras
                ? getAssistantGeneratedAssetImageFineTunePreviewReferences(message)
                : [];
              const assetCopyEditPreviews = shouldRenderStructuredExtras
                ? getAssistantGeneratedAssetCopyEditPreviewReferences(message)
                : [];
              const socialPostReferences = shouldRenderStructuredExtras
                ? getAssistantSocialPostPreviewReferences(message)
                : [];
              const assistantTips =
                shouldRenderStructuredExtras && message.id === latestAssistantMessage?.id
                  ? latestAssistantTips
                  : null;
              if (
                profileEditPreviews.length === 0 &&
                audienceProfileCreationPreviews.length === 0 &&
                onboardingCompletions.length === 0 &&
                assetBatchPreviews.length === 0 &&
                campaignGenerationPreviews.length === 0 &&
                imageGenerationPreviews.length === 0 &&
                visualUniverseRegenerationPreviews.length === 0 &&
                assetImageFineTunePreviews.length === 0 &&
                assetCopyEditPreviews.length === 0 &&
                socialPostReferences.length === 0 &&
                !assistantTips
              ) {
                return null;
              }

              return (
                <div className="space-y-4">
                  <AssistantBrandProfileEditPreviews
                    brandId={brand.id}
                    chatId={routeChatIsPending ? null : (chatId ?? null)}
                    references={profileEditPreviews}
                    resolutionStatuses={profilePreviewResolutionStatuses}
                  />
                  <AssistantAudienceProfileCreationPreviews
                    brandId={brand.id}
                    sourceChatId={routeChatIsPending ? null : (chatId ?? null)}
                    references={audienceProfileCreationPreviews}
                    isStale={isAssistantAudienceProfileCreationProposalStale(messages, message.id)}
                  />
                  <AssistantOnboardingCompletionCards
                    completions={onboardingCompletions}
                    isPromptDisabled={!activeChatReady || isChatBusy}
                    onPrompt={(prompt, creationTool) => void sendPrompt(prompt, { creationTool })}
                  />
                  <AssistantStandaloneAssetBatchPreviews
                    brandId={brand.id}
                    sourceChatId={routeChatIsPending ? null : (chatId ?? null)}
                    references={assetBatchPreviews}
                    isStale={isAssistantStandaloneAssetBatchProposalStale(messages, message.id)}
                    upgradeLabel={assetCreationGate.upgradeLabel}
                    onUpgradeRequired={assetCreationGate.openUpgradeModal}
                    onUseAssetInChat={useAssetInChat}
                  />
                  <AssistantCampaignGenerationPreviews
                    brandId={brand.id}
                    sourceChatId={routeChatIsPending ? null : (chatId ?? null)}
                    references={campaignGenerationPreviews}
                    isStale={isAssistantCampaignGenerationProposalStale(messages, message.id)}
                    upgradeLabel={assetCreationGate.upgradeLabel}
                    onUpgradeRequired={assetCreationGate.openUpgradeModal}
                    campaignBatches={generatedAssetsQuery.data?.batches ?? []}
                  />
                  <AssistantBrandedImageGenerationPreviews
                    brandId={brand.id}
                    chatId={routeChatIsPending ? null : (chatId ?? null)}
                    references={imageGenerationPreviews}
                    isStale={isAssistantBrandedImageGenerationProposalStale(messages, message.id)}
                    upgradeLabel={assetCreationGate.upgradeLabel}
                    onUpgradeRequired={assetCreationGate.openUpgradeModal}
                    onUseImageInChat={useImageInChat}
                  />
                  <AssistantVisualUniverseRegenerationPreviews
                    brandId={brand.id}
                    references={visualUniverseRegenerationPreviews}
                    isStale={isAssistantVisualUniverseRegenerationProposalStale(
                      messages,
                      message.id,
                    )}
                    visualUniverseHref={buildBrandPath(brand.id, "/brand-kit/visual-universe")}
                  />
                  <AssistantGeneratedAssetImageFineTunePreviews
                    brandId={brand.id}
                    chatId={routeChatIsPending ? null : (chatId ?? null)}
                    references={assetImageFineTunePreviews}
                    isStale={isAssistantGeneratedAssetImageFineTuneProposalStale(
                      messages,
                      message.id,
                    )}
                    upgradeLabel={assetCreationGate.upgradeLabel}
                    onUpgradeRequired={assetCreationGate.openUpgradeModal}
                  />
                  <AssistantGeneratedAssetCopyEditPreviews
                    brandId={brand.id}
                    chatId={routeChatIsPending ? null : (chatId ?? null)}
                    references={assetCopyEditPreviews}
                    isStale={isAssistantGeneratedAssetCopyEditProposalStale(messages, message.id)}
                    onOpenEditor={openCopyEditReferenceInEditor}
                  />
                  <AssistantSocialPostPreviews
                    brandId={brand.id}
                    profileLabel={instagramProfileLabel}
                    references={socialPostReferences}
                  />
                  {assistantTips ? (
                    <AssistantSuggestionPills
                      title={assistantTips.title}
                      tips={assistantTips.tips}
                      disabled={!activeChatReady || isChatBusy}
                      onPrompt={(prompt, creationTool) => void sendPrompt(prompt, { creationTool })}
                    />
                  ) : null}
                </div>
              );
            }}
            placeholder={composerPlaceholder}
            runtimeActivity={liveActivity}
            composerContext={
              selectedAssetPreview || selectedReferenceImageSrc ? (
                <div className="space-y-2">
                  {selectedAssetPreview || selectedReferenceImageSrc ? (
                    <div className="group relative inline-flex rounded-2xl bg-muted/55 p-1 shadow-sm">
                      {selectedAssetPreview ? (
                        <SelectedAssetVisual
                          asset={selectedAssetPreview}
                          logoAssets={logoAssets}
                          variant="composer"
                        />
                      ) : selectedReferenceImageSrc ? (
                        <SelectedRawImagePreview
                          src={selectedReferenceImageSrc}
                          aspectRatio={selectedImageReference?.aspectRatio ?? "4:5"}
                          variant="composer"
                        />
                      ) : null}
                      <button
                        type="button"
                        className="absolute -right-1.5 -top-1.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-foreground text-background opacity-0 shadow-sm transition hover:bg-foreground/88 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/45 group-hover:opacity-100 group-focus-within:opacity-100"
                        onClick={() => {
                          setSelectedAssetReferences([]);
                          setSelectedImageReferences([]);
                          setSelectedAssetPreview(null);
                        }}
                        aria-label={intl.formatMessage({
                          id: "pulse.composer.removeReferencedAsset",
                          defaultMessage: "Remove referenced asset",
                        })}
                      >
                        <X className="h-3 w-3" aria-hidden="true" />
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : null
            }
            emptyState={
              <div className="mx-auto flex w-full max-w-5xl flex-col items-center text-center">
                <img
                  src={CHEETAH_SQUARE_LOGO_SRC}
                  alt=""
                  className="mb-6 h-12 w-12 rounded-2xl shadow-[0_18px_40px_-24px_rgba(136,196,0,0.8)]"
                  aria-hidden="true"
                />
                <h1 className="max-w-4xl text-2xl font-black tracking-[-0.04em] text-foreground sm:text-3xl">
                  {greetingTitle}
                </h1>
                <p className="mt-4 max-w-3xl text-sm leading-6 text-muted-foreground">
                  <FormattedMessage
                    id="pulse.emptyState.description"
                    defaultMessage="Ask anything about {brandTitle} or have Cheetah read back your brand profile, recent campaigns, pending reviews, and performance signals."
                    values={{
                      brandTitle: (
                        <span className="font-semibold text-foreground">{brandTitle}</span>
                      ),
                    }}
                  />
                </p>

                <div className="mt-7 flex max-w-4xl flex-wrap justify-center gap-3">
                  {QUICK_PROMPTS.map((item) => (
                    <QuickPromptButton
                      key={item.labelId}
                      label={intl.formatMessage({
                        id: item.labelId,
                        defaultMessage: item.labelDefault,
                      })}
                      icon={item.icon}
                      disabled={!activeChatReady || isChatBusy}
                      onClick={() =>
                        void sendPrompt(
                          intl.formatMessage(
                            {
                              id: item.promptId,
                              defaultMessage: item.promptDefault,
                            },
                            {
                              brandTitle,
                            },
                          ),
                          { creationTool: item.creationTool },
                        )
                      }
                    />
                  ))}
                </div>
              </div>
            }
            onDraftChange={setDraft}
            onSubmit={sendPrompt}
            onStop={stop}
            onClearError={() => {
              setSubmissionError(null);
              clearError();
            }}
            onRetry={retryableUserMessage ? retryResponse : undefined}
            isRetrying={isBusy}
            renderSelectedAssetReference={renderSelectedAssetReference}
            disableRuntimeThoughts
            showActivityParts={false}
          />
        )}

        <p className="pb-6 text-center text-xs text-muted-foreground">
          <FormattedMessage
            id="pulse.footer.disclaimer"
            defaultMessage="Cheetah can make mistakes. Review anything before it ships."
          />
        </p>
      </div>
      <AssistantChatMediaGalleryPanel
        brandId={brand.id}
        chatId={routeChatIsPending ? null : (chatId ?? null)}
        enabled={!isPreparingChat}
        startedCampaignGenerations={startedCampaignGenerations}
        profileLabel={instagramProfileLabel}
        onUseAssetInChat={useAssetInChat}
        onChangeAssetCopyInChat={useAssetCopyInChat}
        onUseImageInChat={useImageInChat}
        onUseImageAsVisualUniverseSeed={useImageAsVisualUniverseSeed}
      />
      {assetCreationGate.upgradeModal}
    </div>
  );
}

type SelectedAssetVisualVariant = "composer" | "message" | "proposal";

function SelectedAssetVisual({
  asset,
  logoAssets,
  variant,
}: {
  asset: ChatGeneratedAsset;
  logoAssets: ChatGeneratedAssetLogoAsset[];
  variant: SelectedAssetVisualVariant;
}) {
  const preview = resolveAssistantSelectedAssetPreview({ asset, logoAssets });
  const widthClass =
    variant === "message"
      ? "w-32 sm:w-40"
      : variant === "proposal"
        ? "w-32 sm:w-36"
        : "w-32 sm:w-36";

  if (preview?.plan) {
    return (
      <div
        className={cn(
          "shrink-0 overflow-hidden rounded-2xl bg-muted shadow-sm ring-1 ring-border/60",
          widthClass,
        )}
        aria-hidden="true"
      >
        <SocialPostPreview
          renderMode="preview"
          platform={preview.plan.platform}
          type={preview.plan.postType}
          plan={preview.plan}
          linkedAssets={preview.linkedAssets}
          brandkit={preview.brandkit}
          showMetadata={false}
          className="w-full"
        />
      </div>
    );
  }

  return (
    <SelectedRawImagePreview
      src={asset.signedUrl}
      aspectRatio={getImageAssetAspectRatio(asset.width, asset.height)}
      variant={variant}
    />
  );
}

function SelectedRawImagePreview({
  src,
  aspectRatio,
  variant,
}: {
  src: string;
  aspectRatio: string;
  variant: SelectedAssetVisualVariant;
}) {
  const sizeClass =
    variant === "message" ? "w-32 sm:w-40" : variant === "proposal" ? "w-32 sm:w-36" : "w-32";

  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-muted shadow-sm ring-1 ring-border/60",
        sizeClass,
      )}
      style={{ aspectRatio: toCssAspectRatio(aspectRatio) }}
      aria-hidden="true"
    >
      <img src={src} alt="" className="h-full w-full object-contain" />
    </div>
  );
}

function getImageAssetAspectRatio(width: number | null, height: number | null) {
  return width && height ? `${width}:${height}` : "4:5";
}

function toCssAspectRatio(aspectRatio: string) {
  return aspectRatio.replace(":", " / ");
}
